import React, { useState } from "react";
import { AnimatePresence } from "framer-motion";
import Landing from "./components/Landing";
import Assessment from "./components/Assessment";
import LoadingAnalysis from "./components/LoadingAnalysis";
import ResultDashboard from "./components/ResultDashboard";
import { calculateScores, getMaturityLevel } from "./utils/scoreCalculator";
import {
  buildInsights,
  buildExecutiveSummary,
} from "./utils/recommendationEngine";
import { questions } from "./data/questions";
import "./AIReadinessAssessment.css";

const STAGES = {
  LANDING: "landing",
  ASSESSMENT: "assessment",
  LOADING: "loading",
  RESULTS: "results",
};

// Your Salesforce Site's Apex REST endpoint
const SALESFORCE_SUBMIT_URL =
  "https://di0000000fs2aeau-dev-ed.my.salesforce-sites.com/services/apexrest/onyxx/assessment";

// Turns { q1: 3, q2: [0,2], ... } + the question bank into the flat
// array of { questionKey, questionText, answerValue, category } records
// the Apex endpoint expects for the Assessment_Answer__c children.
function buildAnswerRecords(answers, otherTexts) {
  return questions.map((q) => {
    const raw = answers[q.id];
    let answerValue;

    if (Array.isArray(raw)) {
      // multi-select: map selected indices back to their option labels
      answerValue = raw.map((i) => q.options[i]).join("; ");
    } else if (typeof raw === "number") {
      answerValue = q.options[raw];
    } else {
      answerValue = "";
    }

    if (otherTexts[q.id]) {
      answerValue += (answerValue ? " - " : "") + otherTexts[q.id];
    }

    return {
      questionKey: q.id,
      questionText: q.title,
      answerValue,
      category: q.weights ? Object.keys(q.weights).join(", ") : "",
    };
  });
}

export default function App() {
  const [stage, setStage] = useState(STAGES.LANDING);
  const [result, setResult] = useState(null);

  // email now comes from the assessment's own email-gate step, not localStorage
  const handleFinish = async (
  answers,
  otherTexts = {},
  email,
  phone
) => {
    const { categoryScores, overallScore } = calculateScores(answers);

    const maturity = getMaturityLevel(overallScore);

    const insightsBase = buildInsights(categoryScores, answers, otherTexts);

    const summary = buildExecutiveSummary(
      overallScore,
      categoryScores,
      maturity.label,
    );

    const payload = {
      email,

      phone,

      overallScore,

      maturity: maturity.label,

      summary,

      aiAdoptionScore: categoryScores.adoption,

      aiAgentScore: categoryScores.agent,

      securityScore: categoryScores.security,

      governanceScore: categoryScores.governance,

      dlpScore: categoryScores.dlp,

      answers: buildAnswerRecords(answers, otherTexts),
    };

    // the results screen from rendering.
    await submitAssessmentToSalesforce(payload);

    setResult({
      overallScore,
      categoryScores,
      insights: {
        ...insightsBase,
        summary,
      },
    });

    setStage(STAGES.LOADING);
  };

  const handleRestart = () => {
    setResult(null);
    setStage(STAGES.LANDING);
  };

  return (
    <div className="aira-app-shell">
      <AnimatePresence mode="wait">
        {stage === STAGES.LANDING && (
          <Landing key="landing" onStart={() => setStage(STAGES.ASSESSMENT)} />
        )}

        {stage === STAGES.ASSESSMENT && (
          <Assessment key="assessment" onFinish={handleFinish} />
        )}

        {stage === STAGES.LOADING && (
          <LoadingAnalysis
            key="loading"
            onDone={() => setStage(STAGES.RESULTS)}
          />
        )}

        {stage === STAGES.RESULTS && result && (
          <ResultDashboard
            key="results"
            overallScore={result.overallScore}
            categoryScores={result.categoryScores}
            insights={result.insights}
            onRestart={handleRestart}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

async function submitAssessmentToSalesforce(payload) {
  if (!payload.email) {
    console.warn("No email captured; skipping Salesforce submission.");
    return;
  }

  try {
    const res = await fetch(SALESFORCE_SUBMIT_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      console.error(
        `Salesforce submission failed (${res.status}):`,
        await res.text(),
      );
    }
  } catch (err) {
    // Guest Site down, CORS misconfigured, network issue, etc.
    // Never throw — this must not block the results screen.
    console.error("Salesforce submission error:", err);
  }
}
